import { useCallback } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

// Smith pages rely on removing all other query parameters when switching tabs (replace),
// whereas Hub pages include `organizationId` query parameter to identify selected tenant (merge)

export function useSetTab({
  method = 'replace',
  prefix = '',
  preserve = [],
}: {
  method?: 'merge' | 'replace' | 'routeParam' | 'none';
  prefix?: string;
  preserve?: string[];
} = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  return useCallback(
    (tab: number) => {
      if (method === 'none') {
        return;
      }
      const toPreserve = preserve.reduce((acc, key) => {
        const value = searchParams.get(prefix + key);
        if (value) {
          acc[prefix + key] = value;
        }
        return acc;
      }, {} as Record<string, string>);
      setSearchParams(
        method === 'merge'
          ? {
              ...Object.fromEntries(searchParams.entries()),
              [prefix + 'tab']: tab.toString(),
            }
          : { [prefix + 'tab']: tab.toString(), ...toPreserve },
        { replace: true }
      );
    },
    [method, searchParams, setSearchParams, prefix]
  );
}

export function useTab({
  defaultIndex,
  prefix = '',
}: {
  defaultIndex?: number;
  prefix?: string;
} = {}) {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get(prefix + 'tab');
  return tabParam && !isNaN(+tabParam) ? +tabParam : defaultIndex ?? 0;
}
export function useSetTabWithRouteParam({
  routeParamName,
  tabIds,
}: {
  routeParamName: string;
  tabIds: string[];
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const paramValue = params[routeParamName];

  return useCallback(
    (tab: number) => {
      if (!paramValue) {
        throw new Error(`Route param ${routeParamName} not found in URL`);
      }
      const tabId = tabIds[tab];
      // Create a new params object with the updated value
      const locationSplit = location.pathname.split('/');
      const paramIndex = locationSplit.lastIndexOf(paramValue);
      if (paramIndex === -1) {
        throw new Error(`Route param ${routeParamName} not found in URL`);
      }
      locationSplit[paramIndex] = tabId;
      navigate(locationSplit.join('/'));
    },
    [tabIds, routeParamName, location.pathname, navigate, paramValue]
  );
}
